import type { generateCode } from '@square/ignition';
import {
  useFernLazy,
  useIgnitionLazy,
  useOasGraphLazy,
} from '@squareup/dex-data-access-oas';
import { getDeveloperApi } from '@squareup/dex-data-shared-developer-api';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { useCodeLanguage } from '@squareup/dex-store-access-shared-main-selectors';
import {
  CodeExampleLanguage,
  codeExampleLanguages,
} from '@squareup/dex-types-oas';
import { SpecificationVersionInfo } from '@squareup/dex-types-oas-path';
import { DocsQueryParams } from '@squareup/dex-types-shared-docs';
import {
  CodeBlockFooterLink,
  CodeDropdownButton,
  CodeDropdownButtonItem,
} from '@squareup/dex-ui';
import { Paragraph10, Paragraph20 } from '@squareup/dex-ui-shared-base';
import { useDecision } from '@squareup/dex-ui-shared-feature-detection';
import { MarkdownCodeBlock } from '@squareup/dex-ui-shared-markdown-components';
import { getNextPublicBaseURL } from '@squareup/dex-utils-environment';
import {
  codeLanguageDisplayMap,
  languageToIgnitionLanguage,
  toFernInput,
} from '@squareup/dex-utils-oas';
import { humanizeName } from '@squareup/dex-utils-text';
import jsonic from 'jsonic';
import { useRouter } from 'next/router';
import React, { FC, PropsWithChildren, useCallback, useMemo } from 'react';

interface MarkdownCodeBlockProps {
  content: string;
  language: string;
  lineNumbers?: boolean;
  openInEditor?: boolean;
  // Overrides for callers that already know the spec version / endpoint
  // identity (e.g. the MDX <EndpointRequestSnippet> adapter resolves
  // these from JSX props, not from the docs route query). When unset,
  // we fall back to the router-derived "latest / unpublished" defaults
  // and parse the endpoint name out of the `language` prefix. Explicit
  // `| undefined` (not just `?`) so callers can pass an undefined value
  // while their resolver is still loading — required for tsconfig
  // `exactOptionalPropertyTypes`.
  specOptionsOverride?: SpecificationVersionInfo | undefined;
  endpointNameOverride?: string | undefined;
  curlOverride?: string | undefined;
}

const isIgnitionEndpoint = (language?: string) => {
  return language?.startsWith('endpoint:') || language?.startsWith('endpoint-');
};

const normalizeEndpointFenceName = (name: string) => {
  const legacyApiOperationName = /^[A-Za-z][\dA-Za-z]+-[A-Z]/.test(name);
  return legacyApiOperationName ? name.split('-').slice(1).join('-') : name;
};

const endpointNameFromLanguage = (language: string) => {
  if (language.startsWith('endpoint:')) {
    return normalizeEndpointFenceName(language.slice('endpoint:'.length));
  }
  if (language.startsWith('endpoint-')) {
    return normalizeEndpointFenceName(language.slice('endpoint-'.length));
  }
  return language.split('-')?.[1] ?? '';
};

const endpointDisplayName = (name: string) => {
  const display = name.includes('-')
    ? name.split('-').join(' ')
    : humanizeName(name);
  return display.charAt(0).toUpperCase() + display.slice(1);
};

const MarkdownIgnitionCodeBlock: FC<
  PropsWithChildren<MarkdownCodeBlockProps>
> = ({
  content,
  language,
  lineNumbers = true,
  openInEditor = false,
  specOptionsOverride,
  endpointNameOverride,
  curlOverride,
}) => {
  const {
    query: {
      [DocsQueryParams.unpublishedOas]: unpublishedOasId,
      [DocsQueryParams.legacyUnpublishedOas]: legacyUnpublishedOasId,
    },
  } = useRouter();

  const unpublishedId = (unpublishedOasId || legacyUnpublishedOasId) as
    | string
    | undefined;

  const ignition = useIgnitionLazy();

  const specOptions = useMemo<SpecificationVersionInfo>(() => {
    if (specOptionsOverride) {
      return specOptionsOverride;
    }
    return {
      grammar: 'open-api-3',
      version: unpublishedId,
      isUnpublished: Boolean(unpublishedId),
      namespace: 'square',
    };
  }, [specOptionsOverride, unpublishedId]);

  const { graph } = useOasGraphLazy(specOptions, getDeveloperApi());

  const [codeLanguage, setCodeLanguage] = useCodeLanguage();

  const requestData = useMemo(() => {
    try {
      return jsonic(content) as Parameters<typeof generateCode>[2];
    } catch {
      return null;
    }
  }, [content]);

  // Authoritative ID for the lookup: prefer an explicit override (MDX
  // adapter knows exactly which Endpoint it resolved), otherwise fall
  // back to the legacy fence language conventions.
  const endpointName =
    endpointNameOverride ?? endpointNameFromLanguage(language);

  const endpoint = useMemo(() => {
    if (!graph) {
      return null;
    }

    return graph.getEndpoint(endpointName);
  }, [graph, endpointName]);

  const { fern } = useFernLazy(
    specOptions,
    endpointName,
    codeLanguage,
    getDeveloperApi()
  );

  const {
    decision: { enabled: fernGoExamples },
  } = useDecision('fern_go_examples');
  const {
    decision: { enabled: fernJavaExamples },
  } = useDecision('fern_java_examples');
  const {
    decision: { enabled: fernPythonExamples },
  } = useDecision('fern_python_examples');
  const {
    decision: { enabled: fernRubyExamples },
  } = useDecision('fern_ruby_examples');
  const {
    decision: { enabled: fernPhpExamples },
  } = useDecision('fern_php_examples');
  const {
    decision: { enabled: fernDotNetExamples },
  } = useDecision('fern_dotnet_examples');
  const {
    decision: { enabled: fernNodeExamples },
  } = useDecision('fern_node_examples');

  const code = useMemo(() => {
    if (!endpoint) {
      return '';
    }

    if (!requestData) {
      return '';
    }

    if (!ignition) {
      return '';
    }

    let shouldUseFern = false;
    switch (codeLanguage) {
      case 'go':
        shouldUseFern = fernGoExamples;
        break;
      case 'java':
        shouldUseFern = fernJavaExamples;
        break;
      case 'python':
        shouldUseFern = fernPythonExamples;
        break;
      case 'ruby':
        shouldUseFern = fernRubyExamples;
        break;
      case 'php':
        shouldUseFern = fernPhpExamples;
        break;
      case 'csharp':
        shouldUseFern = fernDotNetExamples;
        break;
      case 'javascript':
        shouldUseFern = fernNodeExamples;
        break;
      default:
        shouldUseFern = false;
    }

    try {
      if (codeLanguage === 'curl' && curlOverride) {
        return curlOverride;
      }
      if (shouldUseFern && fern) {
        const res = fern.generateSync(toFernInput(requestData, endpoint));
        return res.snippet;
      }
      return ignition.generateCode(
        languageToIgnitionLanguage(codeLanguage) || ignition.Language.curl,
        endpoint,
        requestData
      );
    } catch {
      return content;
    }
  }, [
    endpoint,
    requestData,
    ignition,
    codeLanguage,
    fernGoExamples,
    fernJavaExamples,
    fernPythonExamples,
    fernRubyExamples,
    fernPhpExamples,
    fernDotNetExamples,
    fernNodeExamples,
    fern,
    content,
    curlOverride,
  ]);

  const languagesMemo = useMemo(() => {
    return codeExampleLanguages
      .filter((lang) => {
        // filter out go examples if not released
        if (!fernGoExamples) {
          return lang !== 'go';
        }
        return lang;
      })
      .map((lang) => ({
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        name: codeLanguageDisplayMap.get(lang)!,
        value: lang,
      }));
  }, [fernGoExamples]);

  const onLanguageChangedCallback = useCallback(
    (item: CodeDropdownButtonItem<{ value: CodeExampleLanguage }>) => {
      setCodeLanguage(item.value);
    },
    [setCodeLanguage]
  );

  const apiExplorerLink = useMemo(() => {
    if (!endpoint) {
      return '';
    }

    if (!requestData) {
      return '';
    }

    if (!ignition) {
      return '';
    }

    const params =
      ignition.ApiExplorerParameterHandler.convertCodegenInputToV1(requestData);

    const explorerUrl = `${getNextPublicBaseURL()}/explorer/square/${
      endpoint?.api?.id
    }-api/${endpoint.id}`;

    return ignition.ShareApiHandler.generateLink(
      params,
      explorerUrl,
      endpoint?.squareVersion || '',
      requestData.environment || 'sandbox'
    );
  }, [endpoint, requestData, ignition]);

  const codeDropdown = (
    <CodeDropdownButton
      buttonTestId={'code-block-ignition-language-dropdown'}
      selected={codeLanguageDisplayMap.get(codeLanguage) || ''}
      onSelected={onLanguageChangedCallback}
      items={languagesMemo}
    ></CodeDropdownButton>
  );

  const apiExplorerButton = (
    <CodeBlockFooterLink
      href={apiExplorerLink}
      trackingId={'api-explorer-link'}
      testId={'api-explorer-link'}
      target="_blank"
    >
      <Paragraph10 weight={'semi-bold'}>Try in API Explorer</Paragraph10>
    </CodeBlockFooterLink>
  );

  return (
    <>
      <MarkdownCodeBlock
        content={code}
        language={codeLanguage}
        lineNumbers={lineNumbers}
        floatingHeader={false}
        isLoading={!graph}
        title={
          <Paragraph20 weight={'semi-bold'}>
            {endpointDisplayName(endpointName)}
          </Paragraph20>
        }
        actionGroupBlock={codeDropdown}
        footerBlock={apiExplorerLink && apiExplorerButton}
        openInEditor={openInEditor}
      />
    </>
  );
};

export { MarkdownIgnitionCodeBlock, isIgnitionEndpoint };
